import { Button as ChakraButton, Link } from '@chakra-ui/react';
import throttle from 'lodash/throttle';
import PropTypes from 'prop-types';
import { useMemo } from 'react';

const THROTTLE_MS = 300;

const sizes = {
  large: 'sm',
  normal: 'xs',
  small: '2xs',
};

const getVariant = (color, activated) => {
  switch (color) {
    case 'secondary':
      return activated ? 'subtle' : 'outline';
    case 'tertiary':
    case 'neutral':
      return activated ? 'subtle' : 'ghost';
    case 'destructive-outline':
      return activated ? 'subtle' : 'outline';
    case 'destructive-ghost':
      return activated ? 'subtle' : 'ghost';
    case 'plain':
      return 'plain';
    default:
      return activated ? 'subtle' : 'solid';
  }
};

const getColorPalette = (color) => {
  switch (color) {
    case 'destructive':
    case 'destructive-outline':
    case 'destructive-ghost':
      return 'red';
    case 'neutral':
      return 'gray';
    default:
      return 'primary';
  }
};

const getRadius = (size) => {
  switch (size) {
    case 'small':
      return 'sm';
    default:
      return 'md';
  }
};

const VButton = ({
  children,
  size = 'normal',
  color = 'primary',
  working = false,
  activated = false,
  href = null,
  target = '_blank',
  rel = 'noopener noreferrer',
  onClick,
  prefixIcon = null,
  suffixIcon = null,
  ...props
}) => {
  const variant = getVariant(color, activated);
  const colorPalette = getColorPalette(color);
  const radius = getRadius(size);
  const PrefixIcon = prefixIcon;
  const SuffixIcon = suffixIcon;

  const throttledInvoke = useMemo(
    () =>
      throttle((handler, ...args) => handler?.(...args), THROTTLE_MS, {
        leading: true,
        trailing: false,
      }),
    []
  );

  const content = (
    <>
      {PrefixIcon ? <PrefixIcon aria-hidden="true" /> : null}
      {children}
      {SuffixIcon ? <SuffixIcon aria-hidden="true" /> : null}
    </>
  );

  return (
    <ChakraButton
      size={sizes[size]}
      loading={working}
      colorPalette={colorPalette}
      variant={variant}
      rounded={radius}
      asChild={!!href}
      onClick={onClick ? (event) => throttledInvoke(onClick, event) : undefined}
      {...props}
    >
      {href ? (
        <Link
          href={href}
          target={target}
          rel={rel}
          textDecoration="none"
          _hover={{ textDecoration: 'none' }}
        >
          {content}
        </Link>
      ) : (
        content
      )}
    </ChakraButton>
  );
};

VButton.propTypes = {
  size: PropTypes.oneOf(['large', 'normal', 'small']),
  color: PropTypes.oneOf([
    'primary',
    'secondary',
    'tertiary',
    'destructive',
    'destructive-outline',
    'destructive-ghost',
    'neutral',
    'plain',
  ]),
  working: PropTypes.bool,
  href: PropTypes.string,
  target: PropTypes.string,
  rel: PropTypes.string,
  children: PropTypes.node,
  prefixIcon: PropTypes.elementType,
  suffixIcon: PropTypes.elementType,
};

export default VButton;
